import { Suspense } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    ADD_PERSON,
    EDIT_PERSON
} from '../../modals/personModal/PersonModal.actions';
import { CreateNewCard } from '../cards/CreateNewCard';
import { PersonCard } from '../cards/PersonCard';
import _ from 'lodash';
import messages from './People.messages';
import { getCountry } from '../../../iwIntlProvider/CountryProvider';
import { Dispatch } from 'redux';
import { useQuery } from '@tanstack/react-query';
import { useStateKey } from '../../../iwQueryClient/useStateKey';
import { connect } from 'react-redux';
import { getStateQueryConfig } from '@api/lendingAPI/queryConfigs';
import { Card, H2, H3, Skeleton, Text } from '@ui';
import profileMessages from '../Profile.messages';
import { ErrorBoundary } from '../../../errorBoundary/ErrorBoundary';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    addPerson: () => {
        dispatch({
            type: ADD_PERSON
        });
    },
    editPerson: (uid: string) => {
        dispatch({
            type: EDIT_PERSON,
            uid: uid
        });
    }
});

type PropsFromRedux = ReturnType<typeof mapDispatchToProps>;

const People = ({ editPerson, addPerson }: PropsFromRedux) => {
    return (
        <div className="space-y-xl">
            <div>
                <H2>
                    <FormattedMessage {...profileMessages.peopleTitle} />
                </H2>
                <Text className="mt-xs">
                    <FormattedMessage {...profileMessages.peopleSubtitle} />
                </Text>
            </div>

            <div className="space-y-m">
                <div>
                    <H3>
                        <FormattedMessage
                            {...messages.directorsAndShareholders}
                        />
                    </H3>
                    {getCountry() === 'UK' && (
                        <Text className="mt-xs">
                            <FormattedMessage
                                {...messages.directorsAndShareholdersIntroText}
                            />
                        </Text>
                    )}
                </div>

                <div className="block grid-cols-2 gap-l space-y-m sm:grid sm:space-y-0 lg:grid-cols-3">
                    <ErrorBoundary fallback={() => <CardSkeleton />}>
                        <Suspense fallback={<CardSkeleton />}>
                            <PeopleCards editPerson={editPerson} />
                        </Suspense>
                    </ErrorBoundary>

                    <CreateNewCard
                        onClick={addPerson}
                        label={
                            <FormattedMessage {...messages.addAnotherPerson} />
                        }
                        data-testid="add-new-person"
                    />
                </div>
            </div>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(People);

const PeopleCards = ({ editPerson }: { editPerson: (id: string) => void }) => {
    const stateKey = useStateKey();

    const { data: people } = useQuery({
        ...getStateQueryConfig(stateKey),
        select: (state) => state.data.application?.people,
        suspense: true
    });

    const sortedByApplicant = _.orderBy(
        people,
        (person) => person.roles?.includes('applicant'),
        'desc'
    );

    return (
        <>
            {sortedByApplicant?.map((person, key) => (
                <PersonCard
                    key={key}
                    firstName={person.first_name}
                    lastName={person.last_name}
                    roles={person.roles}
                    isGuarantor={person.roles?.includes('guarantor')}
                    onClick={() => editPerson(person.uid)}
                />
            ))}
        </>
    );
};

const CardSkeleton = () => {
    return (
        <Card data-testid="card-skeleton" className="space-y-s">
            <Skeleton />
            <Skeleton />
            <Skeleton />
            <Skeleton />
        </Card>
    );
};
